import React, { forwardRef, useLayoutEffect, useMemo, useRef, useState } from 'react';

type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'style'>;

interface AutosizeInputProps extends InputProps {
  /** Class for the wrapping span. (optional) */
  className?: string;
  /** Class for the input. (optional) */
  inputClassName?: string;
  /** Style for the input. (optional) */
  inputStyle?: React.CSSProperties;
  /** The minimum width of the input in pixels (optional). Defaults to 0. */
  minWidth?: number;
  /** Style for the wrapping span. (optional) */
  style?: React.CSSProperties;
}

/**
 * Text input that automatically adjusts its width based on the content.
 *
 * Extra props are passed through to the <input> element.
 * @param inputClassName - The class name to apply to the input.
 * @param inputStyle - The style to apply to the input.
 */
export const AutosizeInput = forwardRef<HTMLInputElement | null, AutosizeInputProps>(
  function AutosizeInput(
    {
      className,
      inputClassName,
      inputStyle,
      minWidth,
      placeholder,
      style,
      value,
      ...rest
    }: AutosizeInputProps,
    ref
  ): JSX.Element {
    const { handleSetInputRef, measuredWidth, measurementSpanRef } = useAutosize({
      inputClassName,
      inputStyle,
      minWidth,
      placeholder,
      ref,
      value,
    });

    return (
      <span
        className={className}
        style={{
          ...style,
          alignItems: 'stretch',
          display: 'inline-flex',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <input
          ref={handleSetInputRef}
          className={inputClassName}
          placeholder={placeholder}
          style={{
            ...inputStyle,
            boxSizing: 'content-box',
            maxWidth: '100%',
            width: measuredWidth,
          }}
          value={value}
          {...rest}
        />
        <span
          ref={measurementSpanRef}
          aria-hidden="true"
          style={{
            left: -99999,
            pointerEvents: 'none',
            position: 'absolute',
            top: 0,
            visibility: 'hidden',
            whiteSpace: 'pre',
          }}
        />
      </span>
    );
  }
);

function useAutosize({
  inputClassName,
  inputStyle,
  minWidth = 0,
  placeholder,
  ref,
  value,
}: AutosizeInputProps & { ref: React.Ref<HTMLInputElement> }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const measurementSpanRef = useRef<HTMLSpanElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number>(minWidth);
  const measurementText = useMemo(() => {
    const stringValue = typeof value === 'string' ? value : value == null ? '' : String(value);
    if (stringValue) return stringValue;

    return placeholder ?? '';
  }, [placeholder, value]);

  useLayoutEffect(() => {
    const inputElement = inputRef.current;
    const measurementSpan = measurementSpanRef.current;
    if (!inputElement || !measurementSpan) return;

    const computed = window.getComputedStyle(inputElement);
    measurementSpan.style.font = computed.font;
    measurementSpan.style.letterSpacing = computed.letterSpacing;
    measurementSpan.style.textTransform = computed.textTransform;
    measurementSpan.style.textIndent = computed.textIndent;
    measurementSpan.style.textRendering = computed.textRendering;
    measurementSpan.style.fontKerning = computed.fontKerning;
    measurementSpan.textContent = measurementText || '';

    const nextWidth = Math.ceil(measurementSpan.getBoundingClientRect().width);
    setMeasuredWidth(Math.max(minWidth, nextWidth + 2));
  }, [measurementText, minWidth, inputStyle, inputClassName]);

  function handleSetInputRef(node: HTMLInputElement | null) {
    inputRef.current = node;
    if (!ref) return;
    if (typeof ref === 'function') return ref(node);

    ref.current = node;
  }

  return {
    handleSetInputRef,
    inputRef,
    measuredWidth,
    measurementSpanRef,
  };
}
